import React, {
  useEffect, memo, useCallback, useState, useRef, useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import {
  bool, string, object, func,
} from 'prop-types';
import { FormConsumer } from '@active/react-ui/Form';
import isUndefined from 'lodash/isUndefined';
import omitBy from 'lodash/omitBy';
import noop from 'lodash/noop';
import FormGroup from '@active/react-ui/FormGroup';
import FormField from '@active/react-ui/FormField';
import FormLabel from '@active/react-ui/FormLabel';
import EmailInput from '@active/react-ui/EmailInput';
import Alert from '@active/react-ui/Alert';
import PasswordInput from '@active/react-ui/PasswordInput';
import Locale from '@active/react-utils/i18n/Locale';
import { withI18n } from '@active/react-utils/i18n/LocaleContext';
import { isEmail } from '@active/core/email/validate';
import Grid from '@active/react-ui/Grid/Grid';
import GridItem from '@active/react-ui/GridItem/GridItem';
import { isCryptogram } from '../../../../../shared/utils/commonUtils';
import TwoColsField from '../../../TwoColsField';
import NAME, { validateName } from '../../../Contact/config/FIELDS/NAME';
import BuildMessage from '../../../messages/BuildMessage';

const OrderReceiptAndAccount = ({
  isReceiptAccountEmailReadOnly,
  isShowReceiptAndAccountSection,
  orderReceiptAndAccountTitle,
  requiredEmail,
  isShowCryptogram,
  requiredCryptogram,
  firstName,
  lastName,
  triggers,
  passportClientId,
  i18n,
  locale,
  epid,
  checkEmailExist,
  isExistEmail,
  errorMsgId,
}) => {
  if (!isShowReceiptAndAccountSection) {
    return null;
  }

  const {
    passportWidgetsJsReady,
  } = useSelector(state => state.passPortWidgets || {});

  const renderAPWWidgetCUISignupResult = useRef(null);
  const formRef = useRef(null);
  const [ receiptAccountCryptogram, setReceiptAccountCryptogram ] = useState('');

  const destroyCUISignupWidget = () => {
    if (renderAPWWidgetCUISignupResult.current) {
      renderAPWWidgetCUISignupResult.current.destroy();
    }
  };

  const renderWidget = useCallback(() => {
    const params = {
      locale,
      epid,
      clientId: passportClientId,
      triggers,
      behindMode: true,
      amEighteen: true,
      dobField: { hidden: true, optional: true },
      countryField: { hidden: true, optional: true },
      show: noop,
      hide: noop,
    };
    const prop = omitBy(params, isUndefined);

    destroyCUISignupWidget(); // For props are from async api, So it should rerender the widget
    renderAPWWidgetCUISignupResult.current = window.APWWidget.renderer.renderCUISignupWidgetV2(
      document.getElementById('passport-widget-receiptAccount'), prop);
  }, [ locale, epid, passportClientId ]);

  useEffect(() => {
    if (passportWidgetsJsReady && isShowCryptogram) {
      renderWidget();
    }
    return () => {
      destroyCUISignupWidget();
    };
  }, [ passportWidgetsJsReady, isShowCryptogram, renderWidget ]);

  const handleCryptogramChange = useCallback(({ target: { value } }) => {
    setReceiptAccountCryptogram(value);
  }, [ setReceiptAccountCryptogram ]);

  const handleEmailBlur = useCallback(() => {
    checkEmailExist();
  }, [ checkEmailExist ]);

  const handleCryptogramBlur = useCallback(() => {
    checkEmailExist();
  }, [ checkEmailExist ]);

  const handleCryptogramValidate = useCallback((value) => {
    const { receiptAccountEmail } = formRef.current.values;
    return new Promise((resolve) => {
      if (value && value === receiptAccountEmail) {
        resolve(i18n('checkout.lib.components.orderReceiptAndAccount.cryptogramNotSameWithEmail'));
      } else {
        !value || isCryptogram(value) ? resolve()
          : resolve(i18n('react-ui.validation.invalid'));
      }
    });
  }, [ formRef ]);

  const handleReceiptAccountEmailValidate = useCallback((value) => {
    return new Promise((resolve) => {
      if (!value || isEmail(value)) {
        resolve();
      } else {
        resolve(i18n('react-ui.validation.invalid'));
      }
    });
  }, []);

  const canCreateAcount = useMemo(() => isShowCryptogram && passportWidgetsJsReady,
    [ isShowCryptogram, passportWidgetsJsReady ]);

  const showNameFields = useMemo(() => (!firstName || !lastName || !validateName(firstName) || !validateName(lastName))
  && canCreateAcount, [ firstName, lastName, canCreateAcount ]);

  const emailMsg = useMemo(() => (isExistEmail && isShowCryptogram ? 'checkout.lib.components.orderReceiptAndAccount.emailAddressAssociated' : errorMsgId), [ errorMsgId, isExistEmail, isShowCryptogram ]);

  return (
    <div className="receipt-and-account">
      <h2 className="second-level-title receipt-and-account__title">
        {orderReceiptAndAccountTitle || <Locale id="checkout.lib.components.orderReceiptAndAccount.orderReceiptAndAccount"/>}
      </h2>
      <Grid gap={0} container={false}>
        <GridItem spanXl={8}>
          <Grid rowGap={0} container={false} className="receipt-and-account__content">
            <GridItem>
              <BuildMessage message={emailMsg}/>
              <FormGroup name="receiptAccountEmail" required={requiredEmail} withoutLayout >
                <FormLabel >
                  <Locale id="checkout.lib.components.common.contact.field.email" />
                </FormLabel>
                <FormField
                  onBlur={handleEmailBlur}
                  disabled={isReceiptAccountEmailReadOnly}
                  validate={handleReceiptAccountEmailValidate}
                  autoComplete="off"
                  as={EmailInput}/>
              </FormGroup>
            </GridItem>
            { showNameFields
              && <React.Fragment>
                <TwoColsField field={{ ...NAME.field[0], name: 'receiptAccountFirstName', required: requiredCryptogram }} />
                <TwoColsField field={{ ...NAME.field[1], name: 'receiptAccountLastName', required: requiredCryptogram }} />
              </React.Fragment>
            }
            {
              canCreateAcount
            && <GridItem>
              <Alert theme="inline" className="cryptogram-message cui-mg-t-20">
                <Locale id="checkout.lib.components.orderReceiptAndAccount.saveInfoEnterCryptogram" />
              </Alert>
              <FormGroup name="receiptAccountCryptogram" required={requiredCryptogram} withoutLayout>
                <FormLabel >
                  <Locale id="checkout.lib.components.orderReceiptAndAccount.cryptogram" />
                </FormLabel>
                <FormField
                  onBlur={handleCryptogramBlur}
                  value={receiptAccountCryptogram}
                  onChange={handleCryptogramChange}
                  validate={handleCryptogramValidate}
                  autoComplete="off"
                  hint={i18n('checkout.lib.components.orderReceiptAndAccount.cryptogram.description')}
                  as={PasswordInput}/>
              </FormGroup>
              <div className='form-help-text'><Locale id="checkout.lib.components.text.passwordsNotSameUserName" /></div>
              <div className='form-help-text'><Locale id="checkout.lib.components.text.passwordsNotCommonUsed" /></div>
            </GridItem>
            }
          </Grid>
        </GridItem>
      </Grid>
      <FormConsumer>
        {
          ({
            form,
          }) => {
            formRef.current = form;
          }
        }
      </FormConsumer>
      <div id="passport-widget-receiptAccount"/>
    </div>
  );
};

OrderReceiptAndAccount.propTypes = {
  isShowReceiptAndAccountSection: bool,
  orderReceiptAndAccountTitle: string,
  requiredEmail: bool,
  isShowCryptogram: bool,
  requiredCryptogram: bool,
  triggers: object,
  i18n: func.isRequired,
  locale: string,
  passportClientId: string.isRequired,
  epid: string,
  checkEmailExist: func,
};

OrderReceiptAndAccount.defaultProps = {
  isShowReceiptAndAccountSection: false,
  orderReceiptAndAccountTitle: '',
  requiredEmail: true,
  isShowCryptogram: true,
  requiredCryptogram: true,
  locale: 'en_US',
  checkEmailExist: noop,
};

export default withI18n(memo(OrderReceiptAndAccount));
